import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Instagram, Mail } from 'lucide-react';
import { useBranding } from '../../contexts/BrandingContext';
import { LogoMark } from '../../components/branding/Logo';
import { scrollToSection } from './shared';
import type { HomepageStrings } from '../../i18n/homepage';

type FooterStrings = Pick<
  HomepageStrings,
  | 'footer_cta_h2'
  | 'footer_cta_body'
  | 'footer_cta_primary'
  | 'footer_cta_login'
  | 'footer_brand_blurb'
  | 'footer_contact_link'
  | 'footer_col_invitations'
  | 'footer_col_tools'
  | 'footer_col_company'
  | 'footer_copyright'
>;

const EN_DEFAULTS: FooterStrings = {
  footer_cta_h2: 'Ready to create your invite?',
  footer_cta_body: 'Beautiful digital invitations with RSVP tracking and guest management. Everything included, one simple price.',
  footer_cta_primary: 'Create Your Invite',
  footer_cta_login: 'Log in',
  footer_brand_blurb: 'Beautiful digital wedding invitations with built-in RSVP tracking and guest management.',
  footer_contact_link: 'Send us a message',
  footer_col_invitations: 'Invitations',
  footer_col_tools: 'Tools',
  footer_col_company: 'Company',
  footer_copyright: 'All rights reserved.',
};

export function Footer({ strings }: { strings?: FooterStrings }) {
  const s = strings ?? EN_DEFAULTS;
  const { branding } = useBranding();
  const appName = branding?.app_name || 'Wedsy';
  const navigate = useNavigate();
  const location = useLocation();

  const handleFooterNav = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    if (location.pathname === '/') {
      scrollToSection(e, id);
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <footer className="bg-text-primary">
      {/* CTA Section */}
      <div className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <LogoMark className="w-16 h-16 mx-auto mb-8" />
          <h2
            className="text-3xl md:text-5xl lg:text-6xl text-text-inverse mb-6"
            style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, fontStyle: 'italic' }}
          >
            {s.footer_cta_h2}
          </h2>
          <p className="text-text-inverse/60 text-lg md:text-xl mb-10 max-w-xl mx-auto">
            {s.footer_cta_body}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/create"
              className="btn-hero btn-primary"
            >
              {s.footer_cta_primary}
            </Link>
            <Link
              to="/login"
              className="text-text-inverse/70 font-medium hover:text-text-inverse transition-colors"
            >
              {s.footer_cta_login}
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="border-t border-text-inverse/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 lg:gap-16">

            {/* Left: Brand */}
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-3">
                <LogoMark className="w-8 h-8 opacity-80" />
                <span className="text-text-inverse font-semibold text-lg">{appName}</span>
              </div>
              <p className="text-text-inverse/50 text-sm leading-relaxed">
                {s.footer_brand_blurb}
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-text-inverse/10 hover:bg-text-inverse/20 text-text-inverse/60 hover:text-text-inverse text-sm transition-colors w-fit"
              >
                <Mail className="w-4 h-4 flex-shrink-0" />
                {s.footer_contact_link}
              </a>
              <div className="flex items-center gap-3">
                <a
                  href="https://instagram.com/wedsyinvites"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="w-9 h-9 bg-text-inverse/10 rounded-full flex items-center justify-center hover:bg-text-inverse/20 transition-colors"
                >
                  <Instagram className="w-4 h-4 text-text-inverse/60" />
                </a>
                <a
                  href="https://tiktok.com/@wedsy03"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  className="w-9 h-9 bg-text-inverse/10 rounded-full flex items-center justify-center hover:bg-text-inverse/20 transition-colors"
                >
                  <svg className="w-4 h-4 text-text-inverse/60" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.17 8.17 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Col 1: Invitations */}
            <div>
              <h3 className="text-text-inverse/80 text-xs font-semibold uppercase tracking-widest mb-4">{s.footer_col_invitations}</h3>
              <ul className="space-y-2.5">
                {[
                  { to: '/digital-wedding-invitations', label: 'Digital Wedding Invitations' },
                  { to: '/wedding-invitations-online', label: 'Wedding Invitations Online' },
                  { to: '/electronic-wedding-invitations', label: 'Electronic Invitations' },
                  { to: '/digital-save-the-date-cards', label: 'Save the Date Cards' },
                  { to: '/wedding-invitations-uk', label: 'Wedding Invitations UK' },
                ].map(({ to, label }) => (
                  <li key={to}>
                    <Link to={to} onClick={() => window.scrollTo(0, 0)} className="text-text-inverse/50 hover:text-text-inverse text-sm transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Col 2: Tools */}
            <div>
              <h3 className="text-text-inverse/80 text-xs font-semibold uppercase tracking-widest mb-4">{s.footer_col_tools}</h3>
              <ul className="space-y-2.5">
                {[
                  { to: '/wedding-seating-planner', label: 'Wedding Seating Planner' },
                  { to: '/wedding-rsvp-tracking', label: 'RSVP Tracking' },
                  { to: '/wedding-guest-list', label: 'Guest List Management' },
                  { to: '/day-and-evening-wedding-invitations', label: 'Day & Evening Invitations' },
                  { to: '/online-wedding-invitations-rsvp', label: 'Online RSVP' },
                  { to: '/send-wedding-invitations-online', label: 'Send Invitations Online' },
                ].map(({ to, label }) => (
                  <li key={to}>
                    <Link to={to} onClick={() => window.scrollTo(0, 0)} className="text-text-inverse/50 hover:text-text-inverse text-sm transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Col 3: Company */}
            <div>
              <h3 className="text-text-inverse/80 text-xs font-semibold uppercase tracking-widest mb-4">{s.footer_col_company}</h3>
              <ul className="space-y-2.5">
                {[
                  { to: '/blog', label: 'Blog', scroll: true },
                  { href: '#pricing', label: 'Pricing', scroll: 'pricing' },
                  { href: '#faq', label: 'FAQ', scroll: 'faq' },
                  { to: '/contact', label: 'Contact', scroll: true },
                  { to: '/privacy', label: 'Privacy', scroll: true },
                  { to: '/terms', label: 'Terms', scroll: true },
                ].map((item) => (
                  <li key={item.label}>
                    {'href' in item ? (
                      <a
                        href={item.href}
                        onClick={(e) => handleFooterNav(e, item.scroll as string)}
                        className="text-text-inverse/50 hover:text-text-inverse text-sm transition-colors"
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link to={item.to!} onClick={() => window.scrollTo(0, 0)} className="text-text-inverse/50 hover:text-text-inverse text-sm transition-colors">
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </div>

      {/* Bottom bar: copyright only */}
      <div className="border-t border-text-inverse/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-center">
          <span className="text-text-inverse/50 text-sm" suppressHydrationWarning>
            &copy; {new Date().getFullYear()} {appName}. {s.footer_copyright}
          </span>
        </div>
      </div>
    </footer>
  );
}
